import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Users,
  FileText,
  Calendar,
  MessageSquare,
  ArrowLeft,
  CheckCircle,
  Clock,
  Mail,
  Shield,
  ShieldCheck,
  Download,
  Trash2,
  Search,
  ArrowUpDown,
  Send,
  AlertTriangle,
  Filter,
  Settings,
  Plus,
  X,
  Building,
  Edit,
  Home,
  DollarSign,
  ChevronRight,
  Tag,
  GraduationCap
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Admin() {
  const { user, loading, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loadingData, setLoadingData] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Filter and sort state
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [roleFilter, setRoleFilter] = useState('all');
  const [sortField, setSortField] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  
  // Messaging state
  const [showMessageDialog, setShowMessageDialog] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [messageSubject, setMessageSubject] = useState('');
  const [messageContent, setMessageContent] = useState('');
  const [sendEmail, setSendEmail] = useState(false);
  const [sendNotification, setSendNotification] = useState(true);
  const [sendingMessage, setSendingMessage] = useState(false);
  
  // Delete confirmation
  const [deleteUserId, setDeleteUserId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  
  // Roles management state
  const [roles, setRoles] = useState([]);
  const [newRole, setNewRole] = useState('');
  const [savingRoles, setSavingRoles] = useState(false);

  // Split Report state
  const [listings, setListings] = useState([]);
  const [listingsTotal, setListingsTotal] = useState(0);
  const [listingSearch, setListingSearch] = useState('');
  const [listingStatusFilter, setListingStatusFilter] = useState('all');
  const [editListing, setEditListing] = useState(null);
  const [deleteListingId, setDeleteListingId] = useState(null);

  // User role filter for members
  const [userRoleFilter, setUserRoleFilter] = useState('all');
  const [tierFilter, setTierFilter] = useState('all');

  // Membership tiers state
  const [tiers, setTiers] = useState([]);
  const [editTier, setEditTier] = useState(null);
  const [savingTier, setSavingTier] = useState(false);
  const [newFeature, setNewFeature] = useState('');

  // Email templates state
  const [templates, setTemplates] = useState([]);
  const [editTemplate, setEditTemplate] = useState(null);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [showNewTemplate, setShowNewTemplate] = useState(false);
  const [newTemplate, setNewTemplate] = useState({ name: '', subject: '', content: '', category: 'general' });

  const SPLIT_REPORT_ROLES = ['RE Agent/Salesperson', 'RE Associate Broker', 'RE Broker (Managing Broker / Principal Broker)'];

  useEffect(() => {
    if (!loading && (!user || !isAdmin)) {
      navigate('/dashboard');
      toast.error('Admin access required');
    }
  }, [user, loading, isAdmin, navigate]);

  useEffect(() => {
    if (isAdmin) {
      fetchData();
      fetchRoles();
      fetchTiers();
      fetchTemplates();
    }
  }, [isAdmin]);

  const fetchRoles = async () => {
    try {
      const response = await fetch(`${API}/roles`);
      if (response.ok) {
        const data = await response.json();
        setRoles(data.roles || []);
      }
    } catch (error) {
      console.error('Error fetching roles:', error);
    }
  };

  const addRole = () => {
    if (newRole.trim() && !roles.includes(newRole.trim())) {
      setRoles([...roles, newRole.trim()].sort());
      setNewRole('');
    }
  };

  const removeRole = (role) => {
    setRoles(roles.filter(r => r !== role));
  };

  const saveRoles = async () => {
    setSavingRoles(true);
    try {
      const sessionToken = document.cookie.split('; ').find(row => row.startsWith('session_token='))?.split('=')[1];
      const response = await fetch(`${API}/admin/roles`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          'Cookie': `session_token=${sessionToken}`
        },
        credentials: 'include',
        body: JSON.stringify(roles)
      });
      
      if (response.ok) {
        toast.success('Roles saved successfully');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save roles');
      }
    } catch (error) {
      toast.error('Error saving roles');
    } finally {
      setSavingRoles(false);
    }
  };

  // Membership tiers management
  const fetchTiers = async () => {
    try {
      const response = await fetch(`${API}/admin/tiers`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setTiers(data.tiers || []);
      }
    } catch (error) {
      console.error('Error fetching tiers:', error);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await fetch(`${API}/admin/templates`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setTemplates(data.templates || []);
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  const saveTemplate = async (template) => {
    setSavingTemplate(true);
    try {
      const isNew = !template.template_id;
      const url = isNew ? `${API}/admin/templates` : `${API}/admin/templates/${template.template_id}`;
      const response = await fetch(url, {
        method: isNew ? 'POST' : 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(template)
      });
      if (response.ok) {
        toast.success(isNew ? 'Template created' : 'Template updated');
        setEditTemplate(null);
        setShowNewTemplate(false);
        setNewTemplate({ name: '', subject: '', content: '', category: 'general' });
        fetchTemplates();
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to save template');
      }
    } catch (error) {
      toast.error('Error saving template');
    } finally {
      setSavingTemplate(false);
    }
  };

  const deleteTemplate = async (templateId) => {
    try {
      const response = await fetch(`${API}/admin/templates/${templateId}`, {
        method: 'DELETE', credentials: 'include'
      });
      if (response.ok) {
        toast.success('Template deleted');
        fetchTemplates();
      }
    } catch (error) {
      toast.error('Error deleting template');
    }
  };

  const applyTemplate = (template) => {
    setMessageSubject(template.subject);
    setMessageContent(template.content);
  };

  const saveTier = async (tier) => {
    setSavingTier(true);
    try {
      const response = await fetch(`${API}/admin/tiers/${tier.tier_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: tier.name,
          price: tier.price,
          description: tier.description,
          features: tier.features,
          interval: tier.interval
        })
      });
      if (response.ok) {
        toast.success(`${tier.name} tier updated`);
        setEditTier(null);
        fetchTiers();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update tier');
      }
    } catch (error) {
      toast.error('Error saving tier');
    } finally {
      setSavingTier(false);
    }
  };

  // Filter and sort users when data or filters change
  useEffect(() => {
    let result = [...users];
    
    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(u => 
        u.name?.toLowerCase().includes(query) ||
        u.email?.toLowerCase().includes(query) ||
        u.company?.toLowerCase().includes(query) ||
        u.member_id?.toLowerCase().includes(query)
      );
    }
    
    // Apply status filter
    if (statusFilter !== 'all') {
      result = result.filter(u => u.membership_status === statusFilter);
    }
    
    // Apply role filter
    if (roleFilter !== 'all') {
      result = result.filter(u => u.role === roleFilter);
    }

    // Apply user_role (profession) filter
    if (userRoleFilter !== 'all') {
      if (userRoleFilter === 'split_report') {
        result = result.filter(u => SPLIT_REPORT_ROLES.includes(u.user_role));
      } else if (userRoleFilter === 'verified_agents') {
        result = result.filter(u => u.agent_verified);
      } else if (userRoleFilter === 'unverified_agents') {
        result = result.filter(u => SPLIT_REPORT_ROLES.includes(u.user_role) && !u.agent_verified);
      }
    }
    
    // Apply tier filter
    if (tierFilter !== 'all') {
      result = result.filter(u => (u.membership_tier || 'basic') === tierFilter);
    }
    
    // Apply sorting
    result.sort((a, b) => {
      let aVal = a[sortField] || '';
      let bVal = b[sortField] || '';
      
      if (sortField === 'created_at') {
        aVal = new Date(aVal).getTime();
        bVal = new Date(bVal).getTime();
      } else {
        aVal = String(aVal).toLowerCase();
        bVal = String(bVal).toLowerCase();
      }
      
      if (sortOrder === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });
    
    setFilteredUsers(result);
  }, [users, searchQuery, statusFilter, roleFilter, userRoleFilter, tierFilter, sortField, sortOrder]);

  const fetchData = async () => {
    try {
      const [statsRes, usersRes, messagesRes] = await Promise.all([
        fetch(`${API}/admin/stats`, { credentials: 'include' }),
        fetch(`${API}/admin/users?limit=500`, { credentials: 'include' }),
        fetch(`${API}/contact/messages`, { credentials: 'include' })
      ]);

      if (statsRes.ok) setStats(await statsRes.json());
      if (usersRes.ok) {
        const data = await usersRes.json();
        setUsers(data.users || []);
      }
      if (messagesRes.ok) setMessages(await messagesRes.json());
    } catch (error) {
      console.error('Error fetching admin data:', error);
    } finally {
      setLoadingData(false);
    }
  };

  const updateUserStatus = async (userId, status) => {
    try {
      const response = await fetch(`${API}/admin/users/${userId}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status })
      });
      if (response.ok) {
        toast.success('User status updated');
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to update user');
    }
  };

  const deleteUser = async () => {
    if (!deleteUserId) return;
    
    setDeleting(true);
    try {
      const response = await fetch(`${API}/admin/users/${deleteUserId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (response.ok) {
        toast.success('User deleted');
        setDeleteUserId(null);
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to delete user');
      }
    } catch (error) {
      toast.error('Failed to delete user');
    } finally {
      setDeleting(false);
    }
  };

  const downloadUsers = async () => {
    try {
      let url = `${API}/admin/users/export`;
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (roleFilter !== 'all') params.append('role', roleFilter);
      if (params.toString()) url += `?${params.toString()}`;
      
      const response = await fetch(url, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        
        // Create and download CSV file
        const blob = new Blob([data.csv], { type: 'text/csv' });
        const downloadUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = downloadUrl;
        a.download = `area_members_${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(downloadUrl);
        
        toast.success(`Downloaded ${data.count} members`);
      }
    } catch (error) {
      toast.error('Failed to download');
    }
  };

  const sendMessage = async () => {
    if (!messageSubject.trim() || !messageContent.trim()) {
      toast.error('Please enter subject and message');
      return;
    }
    
    setSendingMessage(true);
    try {
      const response = await fetch(`${API}/admin/messages/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          subject: messageSubject,
          content: messageContent,
          user_ids: selectedUsers,
          send_email: sendEmail,
          send_notification: sendNotification
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`Message sent to ${data.total_recipients} members`);
        setShowMessageDialog(false);
        setMessageSubject('');
        setMessageContent('');
        setSelectedUsers([]);
        setSendEmail(false);
        setSendNotification(true);
      } else {
        toast.error('Failed to send message');
      }
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  const toggleUserSelection = (userId) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const selectAllFiltered = () => {
    if (selectedUsers.length === filteredUsers.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(filteredUsers.map(u => u.user_id));
    }
  };

  const markMessageRead = async (messageId) => {
    try {
      await fetch(`${API}/contact/messages/${messageId}/read`, {
        method: 'PUT',
        credentials: 'include'
      });
      fetchData();
    } catch (error) {
      console.error('Error:', error);
    }
  };

  // Split Report admin functions
  const fetchListings = async () => {
    try {
      const params = new URLSearchParams();
      if (listingSearch) params.append('q', listingSearch);
      if (listingStatusFilter !== 'all') params.append('status', listingStatusFilter);
      params.append('limit', '50');
      const res = await fetch(`${API}/admin/split-report/listings?${params}`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setListings(data.listings || []);
        setListingsTotal(data.total || 0);
      }
    } catch (e) { console.error('Error fetching listings:', e); }
  };

  useEffect(() => {
    if (isAdmin && activeTab === 'split-report') fetchListings();
  }, [isAdmin, activeTab, listingSearch, listingStatusFilter]);

  const saveListingEdit = async () => {
    if (!editListing) return;
    try {
      const res = await fetch(`${API}/admin/split-report/listings/${editListing.listing_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(editListing)
      });
      if (res.ok) {
        toast.success('Listing updated');
        setEditListing(null);
        fetchListings();
      } else { toast.error('Failed to update listing'); }
    } catch (e) { toast.error('Error updating listing'); }
  };

  const deleteListing = async () => {
    if (!deleteListingId) return;
    try {
      const res = await fetch(`${API}/admin/split-report/listings/${deleteListingId}`, {
        method: 'DELETE', credentials: 'include'
      });
      if (res.ok) {
        toast.success('Listing deleted');
        setDeleteListingId(null);
        fetchListings();
      } else { toast.error('Failed to delete listing'); }
    } catch (e) { toast.error('Error deleting listing'); }
  };

  const verifyAgent = async (userId) => {
    try {
      const res = await fetch(`${API}/admin/users/${userId}/verify-agent`, {
        method: 'PUT', credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        fetchData();
      } else { toast.error('Failed to update agent verification'); }
    } catch (e) { toast.error('Error verifying agent'); }
  };

  if (loading || loadingData) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#3D4F7A]"></div>
      </div>
    );
  }

  if (!isAdmin) return null;

  const statusColors = {
    active: 'bg-green-100 text-green-800',
    pending: 'bg-yellow-100 text-yellow-800',
    suspended: 'bg-red-100 text-red-800'
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-panel">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#D42B3C] to-[#a82230] py-8">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-6xl">
          <Link to="/dashboard" className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-4">
            <ArrowLeft className="w-4 h-4" />
            Back to Dashboard
          </Link>
          <div className="flex items-center gap-3">
            <Shield className="w-8 h-8 text-white" />
            <h1 className="font-heading text-3xl font-bold text-white">Admin Panel</h1>
          </div>
          <p className="text-white/80 mt-2">Manage members, content, and site settings</p>
        </div>
      </div>

      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-6xl py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-8">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="users">Members</TabsTrigger>
            <TabsTrigger value="split-report">Split Report</TabsTrigger>
            <TabsTrigger value="messages">Messages</TabsTrigger>
            <TabsTrigger value="settings">Settings</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <Card className="border border-slate-200 shadow-sm">
                <CardContent className="p-6 flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                    <Users className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">{stats?.total_members || 0}</p>
                    <p className="text-sm text-slate-600">Total Members</p>
                  </div>
                </CardContent>
              </Card>
              <Card className="border border-slate-200 shadow-sm">
                <CardContent className="p-6 flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center">
                    <Users className="w-6 h-6 text-slate-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">{stats?.free_members || 0}</p>
                    <p className="text-sm text-slate-600">Free Members</p>
                    <p className="text-xs text-slate-400">{stats?.total_members ? Math.round((stats.free_members || 0) / stats.total_members * 100) : 0}% of total</p>
                  </div>
                </CardContent>
              </Card>
              <Card className="border border-slate-200 shadow-sm">
                <CardContent className="p-6 flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-violet-100 flex items-center justify-center">
                    <Users className="w-6 h-6 text-violet-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">{stats?.pro_members || 0}</p>
                    <p className="text-sm text-slate-600">Pro Members</p>
                    <p className="text-xs text-slate-400">{stats?.total_members ? Math.round((stats.pro_members || 0) / stats.total_members * 100) : 0}% of total</p>
                  </div>
                </CardContent>
              </Card>
              <Card className="border border-slate-200 shadow-sm">
                <CardContent className="p-6 flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
                    <Users className="w-6 h-6 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">{stats?.founder_members || 0}</p>
                    <p className="text-sm text-slate-600">Founder Members</p>
                    <p className="text-xs text-slate-400">{stats?.total_members ? Math.round((stats.founder_members || 0) / stats.total_members * 100) : 0}% of total</p>
                  </div>
                </CardContent>
              </Card>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <Card className="border border-slate-200 shadow-sm">
                <CardContent className="p-6 flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
                    <CheckCircle className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">{stats?.active_members || 0}</p>
                    <p className="text-sm text-slate-600">Active Members</p>
                  </div>
                </CardContent>
              </Card>
              <Card className="border border-slate-200 shadow-sm">
                <CardContent className="p-6 flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-yellow-100 flex items-center justify-center">
                    <Clock className="w-6 h-6 text-yellow-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">{stats?.pending_members || 0}</p>
                    <p className="text-sm text-slate-600">Pending Approval</p>
                  </div>
                </CardContent>
              </Card>
              <Card className="border border-slate-200 shadow-sm">
                <CardContent className="p-6 flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-teal-100 flex items-center justify-center">
                    <Home className="w-6 h-6 text-teal-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">{stats?.total_listings || 0}</p>
                    <p className="text-sm text-slate-600">Split Report Listings</p>
                  </div>
                </CardContent>
              </Card>
              <Card className="border border-slate-200 shadow-sm">
                <CardContent className="p-6 flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-cyan-100 flex items-center justify-center">
                    <ShieldCheck className="w-6 h-6 text-cyan-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">{stats?.verified_agents || 0} / {stats?.split_report_users || 0}</p>
                    <p className="text-sm text-slate-600">Verified Agents</p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Quick Actions */}
            <Card className="border border-slate-200 shadow-sm">
              <CardHeader>
                <CardTitle>Quick Actions</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Button 
                    variant="outline" 
                    className="justify-start"
                    onClick={() => setActiveTab('users')}
                  >
                    <Users className="w-4 h-4 mr-2" />
                    Manage Members
                  </Button>
                  <Button 
                    variant="outline" 
                    className="justify-start"
                    onClick={() => setActiveTab('messages')}
                  >
                    <MessageSquare className="w-4 h-4 mr-2" />
                    View Messages ({stats?.unread_messages || 0} unread)
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users">
            <Card className="border border-slate-200 shadow-sm">
              <CardHeader>
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <CardTitle>Member Management</CardTitle>
                  <div className="flex items-center gap-2">
                    {selectedUsers.length > 0 && (
                      <Button 
                        onClick={() => setShowMessageDialog(true)}
                        className="bg-[#3D4F7A] hover:bg-[#2A3755]"
                        data-testid="message-selected-btn"
                      >
                        <Mail className="w-4 h-4 mr-2" />
                        Message ({selectedUsers.length})
                      </Button>
                    )}
                    <Button 
                      variant="outline" 
                      onClick={() => {
                        setSelectedUsers([]);
                        setShowMessageDialog(true);
                      }}
                      data-testid="message-all-btn"
                    >
                      <Send className="w-4 h-4 mr-2" />
                      Message All
                    </Button>
                    <Button 
                      variant="outline" 
                      onClick={downloadUsers}
                      data-testid="download-members-btn"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download CSV
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {/* Filters */}
                <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
                  <div className="relative">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <Input
                      placeholder="Search by name, email..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-9"
                      data-testid="search-members"
                    />
                  </div>
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger data-testid="filter-status">
                      <Filter className="w-4 h-4 mr-2" />
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="suspended">Suspended</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={roleFilter} onValueChange={setRoleFilter}>
                    <SelectTrigger data-testid="filter-role">
                      <Filter className="w-4 h-4 mr-2" />
                      <SelectValue placeholder="Role" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All System Roles</SelectItem>
                      <SelectItem value="member">Member</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={userRoleFilter} onValueChange={setUserRoleFilter}>
                    <SelectTrigger data-testid="filter-user-role">
                      <Building className="w-4 h-4 mr-2" />
                      <SelectValue placeholder="Profession" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Professions</SelectItem>
                      <SelectItem value="split_report">Split Report Access</SelectItem>
                      <SelectItem value="verified_agents">Verified Agents</SelectItem>
                      <SelectItem value="unverified_agents">Unverified Agents</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={tierFilter} onValueChange={setTierFilter}>
                    <SelectTrigger data-testid="filter-tier">
                      <Tag className="w-4 h-4 mr-2" />
                      <SelectValue placeholder="Tier" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Tiers</SelectItem>
                      <SelectItem value="basic">Basic</SelectItem>
                      <SelectItem value="pro">Pro</SelectItem>
                      <SelectItem value="founder">Founder</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={`${sortField}-${sortOrder}`} onValueChange={(v) => {
                    const [field, order] = v.split('-');
                    setSortField(field);
                    setSortOrder(order);
                  }}>
                    <SelectTrigger data-testid="sort-members">
                      <ArrowUpDown className="w-4 h-4 mr-2" />
                      <SelectValue placeholder="Sort" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="created_at-desc">Newest First</SelectItem>
                      <SelectItem value="created_at-asc">Oldest First</SelectItem>
                      <SelectItem value="name-asc">Name A-Z</SelectItem>
                      <SelectItem value="name-desc">Name Z-A</SelectItem>
                      <SelectItem value="email-asc">Email A-Z</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-center justify-between mb-4">
                  <div className="text-sm text-slate-500">
                    Showing {filteredUsers.length} of {users.length} members
                    {selectedUsers.length > 0 && ` • ${selectedUsers.length} selected`}
                  </div>
                  {filteredUsers.length > 0 && selectedUsers.length === 0 && (
                    <Button size="sm" variant="outline" onClick={() => { selectAllFiltered(true); }} data-testid="select-all-filtered-btn">
                      <Mail className="w-3.5 h-3.5 mr-1.5" />Select All {filteredUsers.length} to Email
                    </Button>
                  )}
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left py-3 px-4">
                          <Checkbox 
                            checked={selectedUsers.length === filteredUsers.length && filteredUsers.length > 0}
                            onCheckedChange={selectAllFiltered}
                            data-testid="select-all-checkbox"
                          />
                        </th>
                        <th className="text-left py-3 px-4 font-medium text-slate-600">Name</th>
                        <th className="text-left py-3 px-4 font-medium text-slate-600">Member ID</th>
                        <th className="text-left py-3 px-4 font-medium text-slate-600">Email</th>
                        <th className="text-left py-3 px-4 font-medium text-slate-600">Profession</th>
                        <th className="text-left py-3 px-4 font-medium text-slate-600">Status</th>
                        <th className="text-left py-3 px-4 font-medium text-slate-600">Tier</th>
                        <th className="text-left py-3 px-4 font-medium text-slate-600">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredUsers.map((u) => (
                        <tr key={u.user_id} className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="py-3 px-4">
                            <Checkbox 
                              checked={selectedUsers.includes(u.user_id)}
                              onCheckedChange={() => toggleUserSelection(u.user_id)}
                              data-testid={`select-user-${u.user_id}`}
                            />
                          </td>
                          <td className="py-3 px-4">
                            <div className="font-medium text-slate-900">{u.name}</div>
                            {u.brokerage_name && <div className="text-xs text-slate-500">{u.brokerage_name}</div>}
                          </td>
                          <td className="py-3 px-4">
                            {u.member_id ? (
                              <span className="font-mono text-sm font-semibold text-[#3D4F7A]">{u.member_id}</span>
                            ) : (
                              <span className="text-xs text-slate-400">—</span>
                            )}
                          </td>
                          <td className="py-3 px-4 text-slate-600 text-sm">{u.email}</td>
                          <td className="py-3 px-4">
                            <div className="flex flex-col gap-1">
                              <span className="text-sm text-slate-700">{u.user_role || '—'}</span>
                              <div className="flex gap-1">
                                {SPLIT_REPORT_ROLES.includes(u.user_role) && (
                                  <Badge className="bg-teal-100 text-teal-700 text-[10px]">Split Report</Badge>
                                )}
                                {u.agent_verified && (
                                  <Badge className="bg-blue-100 text-blue-700 text-[10px]">Verified</Badge>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            <Badge className={statusColors[u.membership_status] || statusColors.pending}>
                              {u.membership_status}
                            </Badge>
                          </td>
                          <td className="py-3 px-4">
                            <Badge className={
                              u.membership_tier === 'founder' ? 'bg-amber-100 text-amber-800' :
                              u.membership_tier === 'pro' ? 'bg-blue-100 text-blue-700' :
                              'bg-slate-100 text-slate-600'
                            }>
                              {u.membership_tier === 'founder' ? 'Founder' : u.membership_tier === 'pro' ? 'Pro' : 'Basic'}
                            </Badge>
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex gap-1 flex-wrap">
                              {u.membership_status === 'pending' && (
                                <Button 
                                  size="sm" 
                                  className="bg-green-600 hover:bg-green-700 h-7 text-xs"
                                  onClick={() => updateUserStatus(u.user_id, 'active')}
                                >
                                  Approve
                                </Button>
                              )}
                              {u.membership_status === 'active' && (
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  className="text-orange-600 border-orange-300 hover:bg-orange-50 h-7 text-xs"
                                  onClick={() => updateUserStatus(u.user_id, 'suspended')}
                                >
                                  Suspend
                                </Button>
                              )}
                              {u.membership_status === 'suspended' && (
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  className="h-7 text-xs"
                                  onClick={() => updateUserStatus(u.user_id, 'active')}
                                >
                                  Reactivate
                                </Button>
                              )}
                              {SPLIT_REPORT_ROLES.includes(u.user_role) && (
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  className={`h-7 text-xs ${u.agent_verified ? 'text-blue-600 border-blue-300' : 'text-slate-500 border-slate-300'}`}
                                  onClick={() => verifyAgent(u.user_id)}
                                  data-testid={`verify-agent-${u.user_id}`}
                                >
                                  <ShieldCheck className="w-3 h-3 mr-1" />
                                  {u.agent_verified ? 'Unverify' : 'Verify'}
                                </Button>
                              )}
                              <Button 
                                size="sm" 
                                variant="outline"
                                className="text-red-600 border-red-300 hover:bg-red-50 h-7"
                                onClick={() => setDeleteUserId(u.user_id)}
                                disabled={u.user_id === user?.user_id}
                                data-testid={`delete-user-${u.user_id}`}
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Split Report Tab */}
          <TabsContent value="split-report">
            <Card className="border border-slate-200 shadow-sm">
              <CardHeader>
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <CardTitle className="flex items-center gap-2">
                    <Home className="w-5 h-5" />
                    Split Report Listings
                    <Badge variant="outline">{listingsTotal} total</Badge>
                  </CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  <div className="relative">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <Input
                      placeholder="Search address, city, agent..."
                      value={listingSearch}
                      onChange={(e) => setListingSearch(e.target.value)}
                      className="pl-9"
                      data-testid="search-listings"
                    />
                  </div>
                  <Select value={listingStatusFilter} onValueChange={setListingStatusFilter}>
                    <SelectTrigger data-testid="filter-listing-status">
                      <Filter className="w-4 h-4 mr-2" />
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="Active">Active</SelectItem>
                      <SelectItem value="Pending">Pending</SelectItem>
                      <SelectItem value="Sold">Sold</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left py-3 px-4 font-medium text-slate-600">Address</th>
                        <th className="text-left py-3 px-4 font-medium text-slate-600">Agent</th>
                        <th className="text-left py-3 px-4 font-medium text-slate-600">Price</th>
                        <th className="text-left py-3 px-4 font-medium text-slate-600">Status</th>
                        <th className="text-left py-3 px-4 font-medium text-slate-600">Commissions</th>
                        <th className="text-left py-3 px-4 font-medium text-slate-600">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {listings.map((l) => (
                        <tr key={l.listing_id} className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="py-3 px-4">
                            <div className="font-medium text-slate-900 text-sm">{l.street_address}</div>
                            <div className="text-xs text-slate-500">{l.city}, {l.state} {l.zip_code}</div>
                          </td>
                          <td className="py-3 px-4 text-sm text-slate-600">{l.agent_name}</td>
                          <td className="py-3 px-4 text-sm font-semibold text-slate-900">${l.sales_price?.toLocaleString()}</td>
                          <td className="py-3 px-4">
                            <Badge className={l.status === 'Active' ? 'bg-green-100 text-green-800' : l.status === 'Sold' ? 'bg-blue-100 text-blue-800' : 'bg-yellow-100 text-yellow-800'}>
                              {l.status}
                            </Badge>
                          </td>
                          <td className="py-3 px-4 text-xs text-slate-500">
                            <div>Buyer: {l.buyer_agent_commission}%</div>
                            <div>Sub: {l.sub_agent_commission}%</div>
                            <div>TC: {l.tc_commission}%</div>
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex gap-1">
                              <Button size="sm" variant="outline" className="h-7 text-xs"
                                onClick={() => setEditListing({...l})}
                                data-testid={`edit-listing-${l.listing_id}`}
                              >
                                <Edit className="w-3 h-3 mr-1" /> Edit
                              </Button>
                              <Button size="sm" variant="outline" className="text-red-600 border-red-300 hover:bg-red-50 h-7 text-xs"
                                onClick={() => setDeleteListingId(l.listing_id)}
                                data-testid={`delete-listing-${l.listing_id}`}
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                      {listings.length === 0 && (
                        <tr><td colSpan={6} className="text-center py-8 text-slate-500">No listings found</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Messages Tab */}
          <TabsContent value="messages">
            <Card className="border border-slate-200 shadow-sm">
              <CardHeader>
                <CardTitle>Contact Messages</CardTitle>
              </CardHeader>
              <CardContent>
                {messages.length === 0 ? (
                  <p className="text-slate-500 text-center py-8">No messages yet</p>
                ) : (
                  <div className="space-y-4">
                    {messages.map((msg) => (
                      <div 
                        key={msg.message_id} 
                        className={`p-4 rounded-lg border ${msg.is_read ? 'bg-slate-50 border-slate-200' : 'bg-blue-50 border-blue-200'}`}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <p className="font-semibold text-slate-900">{msg.name}</p>
                            <p className="text-sm text-slate-600">{msg.email}</p>
                          </div>
                          <div className="text-right">
                            <Badge variant="outline">{msg.inquiry_type}</Badge>
                            <p className="text-xs text-slate-500 mt-1">
                              {new Date(msg.created_at).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        <p className="font-medium text-slate-800 mb-2">{msg.subject}</p>
                        <p className="text-slate-600 text-sm">{msg.message}</p>
                        {!msg.is_read && (
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="mt-3"
                            onClick={() => markMessageRead(msg.message_id)}
                          >
                            Mark as Read
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings" className="space-y-6">
            {/* Affiliate Rules Link */}
            <Card className="border border-slate-200 shadow-sm">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Settings className="w-5 h-5" />
                  Affiliate Program Rules
                </CardTitle>
                <p className="text-sm text-slate-500">Configure attribution, commissions, payouts, fraud protection, and partner requirements.</p>
              </CardHeader>
              <CardContent>
                <a href="/admin/settings/affiliate-rules">
                  <Button className="bg-[#1e3a5f] hover:bg-[#152d4a] text-white" data-testid="affiliate-rules-link">
                    Manage Affiliate Rules
                    <ChevronRight className="w-4 h-4 ml-2" />
                  </Button>
                </a>
              </CardContent>
            </Card>
            {/* Affiliate Management Link */}
            <Card className="border border-slate-200 shadow-sm">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  Affiliate Management
                </CardTitle>
                <p className="text-sm text-slate-500">View affiliates, referrals, conversions, payouts, and fraud flags.</p>
              </CardHeader>
              <CardContent className="flex gap-3 flex-wrap">
                <a href="/admin/affiliates">
                  <Button className="bg-[#1e3a5f] hover:bg-[#152d4a] text-white" data-testid="admin-affiliates-link">
                    Manage Affiliates
                    <ChevronRight className="w-4 h-4 ml-2" />
                  </Button>
                </a>
                <a href="/admin/commission-strategy">
                  <Button variant="outline" data-testid="admin-commission-strategy-link">
                    <DollarSign className="w-4 h-4 mr-2" />
                    Commission Strategy
                    <ChevronRight className="w-4 h-4 ml-2" />
                  </Button>
                </a>
                <a href="/admin/revenue-projections">
                  <Button variant="outline" data-testid="admin-revenue-projections-link">
                    Revenue Projections
                    <ChevronRight className="w-4 h-4 ml-2" />
                  </Button>
                </a>
                <a href="/admin/growth-playbook">
                  <Button variant="outline" data-testid="admin-growth-playbook-link">
                    Growth Playbook
                    <ChevronRight className="w-4 h-4 ml-2" />
                  </Button>
                </a>
                <a href="/admin/fraud-flags">
                  <Button variant="outline" data-testid="admin-fraud-flags-link">
                    Fraud Flags
                    <ChevronRight className="w-4 h-4 ml-2" />
                  </Button>
                </a>
                <a href="/admin/affiliate-emails">
                  <Button variant="outline" data-testid="admin-affiliate-emails-link">
                    Affiliate Emails
                    <ChevronRight className="w-4 h-4 ml-2" />
                  </Button>
                </a>
                <a href="/admin/payouts">
                  <Button variant="outline" data-testid="admin-payouts-link">
                    Payout Processing
                    <ChevronRight className="w-4 h-4 ml-2" />
                  </Button>
                </a>
              </CardContent>
            </Card>
            {/* Partner Directory Management */}
            <Card className="border border-slate-200 shadow-sm">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Tag className="w-5 h-5" />
                  Partner Directory
                </CardTitle>
                <p className="text-sm text-slate-500">Manage categories, partner listings, ad placements, badges, and affiliate disclosure for the curated partner directory.</p>
              </CardHeader>
              <CardContent>
                <a href="/admin/partners">
                  <Button className="bg-[#1e3a5f] hover:bg-[#152d4a] text-white" data-testid="admin-partners-link">
                    Manage Partner Directory
                    <ChevronRight className="w-4 h-4 ml-2" />
                  </Button>
                </a>
              </CardContent>
            </Card>
            {/* Scheduled Reports */}
            <Card className="border border-slate-200 shadow-sm">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="w-5 h-5" />
                  Scheduled Reports
                </CardTitle>
                <p className="text-sm text-slate-500">Configure automated CSV reports delivered to your inbox on a weekly or monthly schedule.</p>
              </CardHeader>
              <CardContent>
                <a href="/admin/scheduled-reports">
                  <Button className="bg-[#1e3a5f] hover:bg-[#152d4a] text-white" data-testid="admin-scheduled-reports-link">
                    Manage Schedules
                    <ChevronRight className="w-4 h-4 ml-2" />
                  </Button>
                </a>
              </CardContent>
            </Card>
            {/* Membership Tiers Management */}
            <Card className="border border-slate-200 shadow-sm">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <DollarSign className="w-5 h-5" />
                  Membership Tiers
                </CardTitle>
                <p className="text-sm text-slate-500">Manage tier names, pricing, and included features shown during registration.</p>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {tiers.map((tier) => (
                    <div key={tier.tier_id} className="border rounded-lg p-4 relative" data-testid={`tier-card-${tier.tier_id}`}>
                      {editTier?.tier_id === tier.tier_id ? (
                        <div className="space-y-3">
                          <div>
                            <label className="text-xs font-medium text-slate-500 mb-1 block">Tier Name</label>
                            <Input
                              value={editTier.name}
                              onChange={(e) => setEditTier({...editTier, name: e.target.value})}
                              data-testid={`tier-edit-name-${tier.tier_id}`}
                            />
                          </div>
                          <div>
                            <label className="text-xs font-medium text-slate-500 mb-1 block">Price ($)</label>
                            <Input
                              type="number"
                              min="0"
                              value={editTier.price}
                              onChange={(e) => setEditTier({...editTier, price: parseFloat(e.target.value) || 0})}
                              data-testid={`tier-edit-price-${tier.tier_id}`}
                            />
                          </div>
                          <div>
                            <label className="text-xs font-medium text-slate-500 mb-1 block">Billing Interval</label>
                            <Select value={editTier.interval || 'none'} onValueChange={(v) => setEditTier({...editTier, interval: v === 'none' ? null : v})}>
                              <SelectTrigger data-testid={`tier-edit-interval-${tier.tier_id}`}><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none">One-time / Free</SelectItem>
                                <SelectItem value="month">Monthly</SelectItem>
                                <SelectItem value="year">Yearly</SelectItem>
                                <SelectItem value="lifetime">Lifetime</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <label className="text-xs font-medium text-slate-500 mb-1 block">Description</label>
                            <Input
                              value={editTier.description || ''}
                              onChange={(e) => setEditTier({...editTier, description: e.target.value})}
                              data-testid={`tier-edit-desc-${tier.tier_id}`}
                            />
                          </div>
                          <div>
                            <label className="text-xs font-medium text-slate-500 mb-1 block">Included Features</label>
                            <div className="space-y-1 mb-2">
                              {editTier.features.map((f, i) => (
                                <div key={i} className="flex items-center gap-1 bg-slate-50 rounded px-2 py-1">
                                  <span className="text-sm flex-1">{f}</span>
                                  <button onClick={() => setEditTier({...editTier, features: editTier.features.filter((_, idx) => idx !== i)})} className="text-red-400 hover:text-red-600">
                                    <X className="w-3 h-3" />
                                  </button>
                                </div>
                              ))}
                            </div>
                            <div className="flex gap-1">
                              <Input
                                placeholder="Add feature..."
                                value={newFeature}
                                onChange={(e) => setNewFeature(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' && newFeature.trim()) {
                                    setEditTier({...editTier, features: [...editTier.features, newFeature.trim()]});
                                    setNewFeature('');
                                  }
                                }}
                                className="text-sm"
                                data-testid={`tier-add-feature-${tier.tier_id}`}
                              />
                              <Button size="sm" variant="outline" onClick={() => {
                                if (newFeature.trim()) {
                                  setEditTier({...editTier, features: [...editTier.features, newFeature.trim()]});
                                  setNewFeature('');
                                }
                              }}>
                                <Plus className="w-3 h-3" />
                              </Button>
                            </div>
                          </div>
                          <div className="flex gap-2 pt-2">
                            <Button size="sm" className="flex-1 bg-[#3D4F7A] hover:bg-[#2A3755]" disabled={savingTier} onClick={() => saveTier(editTier)} data-testid={`tier-save-${tier.tier_id}`}>
                              {savingTier ? 'Saving...' : 'Save'}
                            </Button>
                            <Button size="sm" variant="outline" className="flex-1" onClick={() => { setEditTier(null); setNewFeature(''); }} data-testid={`tier-cancel-${tier.tier_id}`}>
                              Cancel
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="flex items-start justify-between mb-2">
                            <div>
                              <h4 className="font-semibold text-base">{tier.name}</h4>
                              <span className="text-lg font-bold text-[#3D4F7A]">
                                {tier.price === 0 ? 'Free' : `$${tier.price}`}
                                {tier.interval && tier.price > 0 && <span className="text-xs font-normal text-slate-400">/{tier.interval}</span>}
                              </span>
                            </div>
                            <Button size="sm" variant="ghost" onClick={() => { setEditTier({...tier}); setNewFeature(''); }} data-testid={`tier-edit-btn-${tier.tier_id}`}>
                              <Edit className="w-4 h-4" />
                            </Button>
                          </div>
                          <p className="text-xs text-slate-500 mb-3">{tier.description}</p>
                          <ul className="space-y-1">
                            {tier.features.map((f, i) => (
                              <li key={i} className="text-xs text-slate-600 flex items-center gap-1.5">
                                <CheckCircle className="w-3 h-3 text-green-500 shrink-0" />
                                {f}
                              </li>
                            ))}
                          </ul>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Email Templates Management */}
            <Card className="border border-slate-200 shadow-sm">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <Mail className="w-5 h-5" />
                    Email Templates
                  </CardTitle>
                  <Button size="sm" onClick={() => { setShowNewTemplate(true); setEditTemplate(null); }} data-testid="new-template-btn">
                    <Plus className="w-4 h-4 mr-1" />New Template
                  </Button>
                </div>
                <p className="text-sm text-slate-500">Manage reusable email templates for bulk messaging.</p>
              </CardHeader>
              <CardContent>
                {/* New Template Form */}
                {showNewTemplate && (
                  <div className="border border-blue-200 bg-blue-50/50 rounded-lg p-4 mb-4 space-y-3" data-testid="new-template-form">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs font-medium text-slate-500 mb-1 block">Template Name</label>
                        <Input value={newTemplate.name} onChange={(e) => setNewTemplate(prev => ({...prev, name: e.target.value}))} placeholder="e.g., Monthly Newsletter" data-testid="new-tpl-name" />
                      </div>
                      <div>
                        <label className="text-xs font-medium text-slate-500 mb-1 block">Category</label>
                        <Select value={newTemplate.category} onValueChange={(v) => setNewTemplate(prev => ({...prev, category: v}))}>
                          <SelectTrigger data-testid="new-tpl-category"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="general">General</SelectItem>
                            <SelectItem value="onboarding">Onboarding</SelectItem>
                            <SelectItem value="billing">Billing</SelectItem>
                            <SelectItem value="events">Events</SelectItem>
                            <SelectItem value="announcements">Announcements</SelectItem>
                            <SelectItem value="account">Account</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-slate-500 mb-1 block">Subject Line</label>
                      <Input value={newTemplate.subject} onChange={(e) => setNewTemplate(prev => ({...prev, subject: e.target.value}))} placeholder="Email subject..." data-testid="new-tpl-subject" />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-slate-500 mb-1 block">Content</label>
                      <Textarea value={newTemplate.content} onChange={(e) => setNewTemplate(prev => ({...prev, content: e.target.value}))} placeholder="Email body content..." className="min-h-[100px]" data-testid="new-tpl-content" />
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" className="bg-[#3D4F7A] hover:bg-[#2A3755]" disabled={savingTemplate || !newTemplate.name.trim() || !newTemplate.subject.trim()} onClick={() => saveTemplate(newTemplate)} data-testid="save-new-tpl">
                        {savingTemplate ? 'Saving...' : 'Create Template'}
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => { setShowNewTemplate(false); setNewTemplate({ name: '', subject: '', content: '', category: 'general' }); }}>Cancel</Button>
                    </div>
                  </div>
                )}

                {/* Template List */}
                <div className="space-y-3">
                  {templates.map((t) => (
                    <div key={t.template_id} className="border border-slate-200 rounded-lg p-4" data-testid={`template-${t.template_id}`}>
                      {editTemplate?.template_id === t.template_id ? (
                        <div className="space-y-3">
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="text-xs font-medium text-slate-500 mb-1 block">Name</label>
                              <Input value={editTemplate.name} onChange={(e) => setEditTemplate(prev => ({...prev, name: e.target.value}))} data-testid={`edit-tpl-name-${t.template_id}`} />
                            </div>
                            <div>
                              <label className="text-xs font-medium text-slate-500 mb-1 block">Category</label>
                              <Select value={editTemplate.category} onValueChange={(v) => setEditTemplate(prev => ({...prev, category: v}))}>
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="general">General</SelectItem>
                                  <SelectItem value="onboarding">Onboarding</SelectItem>
                                  <SelectItem value="billing">Billing</SelectItem>
                                  <SelectItem value="events">Events</SelectItem>
                                  <SelectItem value="announcements">Announcements</SelectItem>
                                  <SelectItem value="account">Account</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                          <div>
                            <label className="text-xs font-medium text-slate-500 mb-1 block">Subject</label>
                            <Input value={editTemplate.subject} onChange={(e) => setEditTemplate(prev => ({...prev, subject: e.target.value}))} />
                          </div>
                          <div>
                            <label className="text-xs font-medium text-slate-500 mb-1 block">Content</label>
                            <Textarea value={editTemplate.content} onChange={(e) => setEditTemplate(prev => ({...prev, content: e.target.value}))} className="min-h-[100px]" />
                          </div>
                          <div className="flex gap-2">
                            <Button size="sm" className="bg-[#3D4F7A] hover:bg-[#2A3755]" disabled={savingTemplate} onClick={() => saveTemplate(editTemplate)}>
                              {savingTemplate ? 'Saving...' : 'Save'}
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => setEditTemplate(null)}>Cancel</Button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className="font-semibold text-sm text-slate-900">{t.name}</h4>
                              <Badge className="bg-slate-100 text-slate-600 text-[10px]">{t.category}</Badge>
                            </div>
                            <p className="text-xs text-slate-500 mb-1">Subject: {t.subject}</p>
                            <p className="text-xs text-slate-400 line-clamp-2">{t.content}</p>
                          </div>
                          <div className="flex gap-1 shrink-0">
                            <Button size="sm" variant="ghost" onClick={() => { setEditTemplate({...t}); setShowNewTemplate(false); }} data-testid={`edit-tpl-${t.template_id}`}>
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-700" onClick={() => deleteTemplate(t.template_id)} data-testid={`delete-tpl-${t.template_id}`}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                  {templates.length === 0 && (
                    <p className="text-sm text-slate-400 text-center py-6">No templates yet. Create one to speed up bulk messaging.</p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Roles Management */}
            <Card className="border border-slate-200 shadow-sm">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Settings className="w-5 h-5" />
                  Manage Registration Roles
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-slate-600 mb-4">
                  These roles appear in the registration form dropdown. Users can search and select from this list.
                </p>
                
                {/* Add new role */}
                <div className="flex gap-2 mb-6">
                  <Input
                    placeholder="Enter new role..."
                    value={newRole}
                    onChange={(e) => setNewRole(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && addRole()}
                    className="flex-1"
                    data-testid="new-role-input"
                  />
                  <Button onClick={addRole} className="bg-[#3D4F7A] hover:bg-[#2A3755]">
                    <Plus className="w-4 h-4 mr-1" /> Add
                  </Button>
                </div>

                {/* Role list */}
                <div className="border rounded-lg max-h-96 overflow-y-auto mb-4">
                  {roles.length === 0 ? (
                    <div className="p-4 text-center text-slate-500">No roles defined</div>
                  ) : (
                    <div className="divide-y">
                      {roles.map((role, index) => (
                        <div key={index} className="flex items-center justify-between px-4 py-2 hover:bg-slate-50">
                          <span className="text-sm">{role}</span>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => removeRole(role)}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-500">{roles.length} roles total</span>
                  <Button 
                    onClick={saveRoles} 
                    disabled={savingRoles}
                    className="bg-[#3D4F7A] hover:bg-[#2A3755]"
                    data-testid="save-roles-btn"
                  >
                    {savingRoles ? 'Saving...' : 'Save Changes'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Message Dialog */}
      <Dialog open={showMessageDialog} onOpenChange={setShowMessageDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {selectedUsers.length > 0 
                ? `Send Message to ${selectedUsers.length} Member${selectedUsers.length > 1 ? 's' : ''}`
                : 'Send Message to All Active Members'
              }
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {templates.length > 0 && (
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">Use Template</label>
                <Select onValueChange={(id) => { const t = templates.find(t => t.template_id === id); if (t) applyTemplate(t); }}>
                  <SelectTrigger data-testid="template-picker"><SelectValue placeholder="Choose a template..." /></SelectTrigger>
                  <SelectContent>
                    {templates.map((t) => (
                      <SelectItem key={t.template_id} value={t.template_id}>
                        {t.name} <span className="text-slate-400 ml-1 text-xs">({t.category})</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Subject</label>
              <Input
                placeholder="Enter message subject..."
                value={messageSubject}
                onChange={(e) => setMessageSubject(e.target.value)}
                data-testid="message-subject"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Message</label>
              <Textarea
                placeholder="Write your message..."
                value={messageContent}
                onChange={(e) => setMessageContent(e.target.value)}
                rows={5}
                data-testid="message-content"
              />
            </div>
            <div className="space-y-3 pt-2">
              <div className="flex items-center gap-2">
                <Checkbox 
                  id="send-notification"
                  checked={sendNotification}
                  onCheckedChange={setSendNotification}
                />
                <label htmlFor="send-notification" className="text-sm text-slate-700">
                  Send as site notification (bell icon)
                </label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox 
                  id="send-email"
                  checked={sendEmail}
                  onCheckedChange={setSendEmail}
                />
                <label htmlFor="send-email" className="text-sm text-slate-700">
                  Also send via email
                </label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMessageDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={sendMessage}
              disabled={sendingMessage || !messageSubject.trim() || !messageContent.trim()}
              className="bg-[#3D4F7A] hover:bg-[#2A3755]"
              data-testid="send-message-btn"
            >
              {sendingMessage ? 'Sending...' : 'Send Message'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteUserId} onOpenChange={() => setDeleteUserId(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Delete Member
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-slate-600">
              Are you sure you want to delete this member? This action cannot be undone. 
              All their posts, comments, and data will be permanently removed.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteUserId(null)}>
              Cancel
            </Button>
            <Button 
              onClick={deleteUser}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-btn"
            >
              {deleting ? 'Deleting...' : 'Delete Member'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Listing Dialog */}
      <Dialog open={!!editListing} onOpenChange={() => setEditListing(null)}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Listing</DialogTitle>
          </DialogHeader>
          {editListing && (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Street Address</label>
                <Input value={editListing.street_address || ''} onChange={(e) => setEditListing({...editListing, street_address: e.target.value})} />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-2">
                  <label className="text-sm font-medium">City</label>
                  <Input value={editListing.city || ''} onChange={(e) => setEditListing({...editListing, city: e.target.value})} />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">State</label>
                  <Input value={editListing.state || ''} onChange={(e) => setEditListing({...editListing, state: e.target.value})} />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Zip</label>
                  <Input value={editListing.zip_code || ''} onChange={(e) => setEditListing({...editListing, zip_code: e.target.value})} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Sales Price ($)</label>
                  <Input type="number" value={editListing.sales_price || ''} onChange={(e) => setEditListing({...editListing, sales_price: parseFloat(e.target.value) || 0})} />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Status</label>
                  <Select value={editListing.status} onValueChange={(v) => setEditListing({...editListing, status: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Active">Active</SelectItem>
                      <SelectItem value="Pending">Pending</SelectItem>
                      <SelectItem value="Sold">Sold</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Buyer Agent %</label>
                  <Input type="number" step="0.01" value={editListing.buyer_agent_commission || ''} onChange={(e) => setEditListing({...editListing, buyer_agent_commission: parseFloat(e.target.value) || 0})} />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Sub Agent %</label>
                  <Input type="number" step="0.01" value={editListing.sub_agent_commission || ''} onChange={(e) => setEditListing({...editListing, sub_agent_commission: parseFloat(e.target.value) || 0})} />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">TC %</label>
                  <Input type="number" step="0.01" value={editListing.tc_commission || ''} onChange={(e) => setEditListing({...editListing, tc_commission: parseFloat(e.target.value) || 0})} />
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Notes</label>
                <Textarea value={editListing.notes || ''} onChange={(e) => setEditListing({...editListing, notes: e.target.value})} rows={3} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditListing(null)}>Cancel</Button>
            <Button onClick={saveListingEdit} className="bg-[#3D4F7A] hover:bg-[#2A3755]" data-testid="save-listing-edit-btn">Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Listing Dialog */}
      <Dialog open={!!deleteListingId} onOpenChange={() => setDeleteListingId(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Delete Listing
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-slate-600">
              Are you sure you want to delete this listing? This action cannot be undone.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteListingId(null)}>Cancel</Button>
            <Button onClick={deleteListing} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-listing-btn">
              Delete Listing
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
